import type { Event } from '@sentry/angular';

const FILTERED = '[Filtered]';
const MAX_DEPTH = 12;
const SIGN_IN_PATH = /(\/(?:login|password-recovery)\/)[^/?#\s"']+/g;
const SECRET_PARAM = /([?&#;](?:access_token|auth_token|authToken|token|code|password|client_secret)=)[^&#;\s"']+/gi;
const SECRET_KEY = /^(access_token|auth_token|authToken|token|password|current_password|new_password|confirm_password|password_confirmation|client_secret)$/i;

/** Blanks sign-in keys and passwords in a URL or any text that contains one. */
export function scrubSecrets(text: string, mask = FILTERED): string {
  return text.replace(SIGN_IN_PATH, `$1${mask}`).replace(SECRET_PARAM, `$1${mask}`);
}

/** Copy of a Sentry event (error or transaction) with every sign-in key and password blanked. */
export function scrubSentryEvent<T extends Event>(event: T): T {
  const { sdkProcessingMetadata, ...rest } = event;
  const scrubbed = scrubValue(rest, 0) as T;
  return sdkProcessingMetadata ? { ...scrubbed, sdkProcessingMetadata } : scrubbed;
}

/** Copy of a span Sentry sends on its own (such as an interaction span) with every sign-in key blanked. */
export function scrubSentrySpan<T extends object>(span: T): T {
  return scrubValue(span, 0) as T;
}

function scrubValue(value: unknown, depth: number): unknown {
  if (typeof value === 'string') {
    return scrubSecrets(value);
  }
  if (value === null || typeof value !== 'object' || depth > MAX_DEPTH) {
    return value;
  }
  if (Array.isArray(value)) {
    if (value.length === 2 && typeof value[0] === 'string' && SECRET_KEY.test(value[0])) {
      return [value[0], FILTERED];
    }
    return value.map(item => scrubValue(item, depth + 1));
  }
  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [key, SECRET_KEY.test(key) ? FILTERED : scrubValue(item, depth + 1)])
  );
}
